/**
 * AppShell — sidebar + content frame for non-editor pages (ingest, review, preview).
 *
 * Why this exists: ingest routes used to render full-screen, hiding the sidebar
 * and breaking the system shell. AppShell reuses Sidebar + theme + mobile drawer
 * behavior from Layout, but slots arbitrary children where MainPanel would go,
 * so ingest pages live inside the same nav context as the workspace.
 */

import { useEffect } from 'react'
import { Menu, Search } from 'lucide-react'
import { useAppStore } from '../../stores/app-store'
import { useIsMobile } from '../../hooks/use-is-mobile'
import { useSwipeGesture } from '../../hooks/use-swipe-gesture'
import { Sidebar } from './sidebar'
import { CommandPalette } from '../command-palette/command-palette'
import { StorageDrawer } from '../storage/storage-drawer'
import { cn } from '../../lib/utils'

interface AppShellProps {
  children: React.ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const {
    theme,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    toggleCommandPalette,
  } = useAppStore()
  const isMobile = useIsMobile()
  const isDark = theme === 'dark'

  useSwipeGesture({
    onSwipeRight: () => setMobileSidebarOpen(true),
    onSwipeLeft: () => {},
    onBackdropSwipeLeft: () => setMobileSidebarOpen(false),
    enabled: isMobile,
  })

  useEffect(() => {
    document.body.style.overflow = mobileSidebarOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileSidebarOpen])

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
      root.classList.remove('light')
    } else {
      root.classList.add('light')
      root.classList.remove('dark')
    }
  }, [theme])

  if (isMobile) {
    return (
      <div
        className={cn(
          'flex h-screen flex-col overflow-hidden font-sans antialiased',
          isDark ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900',
        )}
      >
        {/* Mobile header — mirrors MainPanel's header for visual continuity */}
        <div
          className={cn(
            'flex items-center justify-between px-2 py-2 border-b shrink-0',
            isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
          )}
        >
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className={cn(
              'flex items-center justify-center rounded-lg p-2',
              isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100',
            )}
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-gradient-to-br from-brand-500 to-brand-700">
              <span className="text-[8px] font-bold text-white">A</span>
            </div>
            <span className={cn('text-sm font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
              AgentWiki
            </span>
          </div>

          <button
            onClick={toggleCommandPalette}
            className={cn(
              'flex items-center justify-center rounded-lg p-2',
              isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100',
            )}
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Page content */}
        <div className="flex-1 overflow-auto">{children}</div>

        {/* Sidebar drawer */}
        <div
          className={cn('fixed inset-0 z-50 bg-black/60 backdrop-blur-sm drawer-backdrop', mobileSidebarOpen && 'drawer-open')}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden={!mobileSidebarOpen}
        />
        <div
          className={cn('fixed inset-y-0 left-0 z-50 h-full w-[280px] max-w-[85vw] drawer-left', mobileSidebarOpen && 'drawer-open')}
          aria-hidden={!mobileSidebarOpen}
        >
          <Sidebar />
        </div>

        <CommandPalette />
        <StorageDrawer />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex h-screen overflow-hidden font-sans antialiased',
        isDark ? 'bg-surface-0 text-neutral-100' : 'bg-neutral-50 text-neutral-900',
      )}
    >
      <Sidebar />
      <div className="flex-1 overflow-auto">{children}</div>
      <CommandPalette />
      <StorageDrawer />
    </div>
  )
}
